import React, { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { SuperAdminDashboardService } from 'utility/services/super-admin-dashboard'
import { SUPER_ADMIN_FLOW_ENABLED } from 'utility/super-admin-flow'
import {
    isMissingApi,
    sampleCompanies,
    sampleOverview,
    sampleSignIns,
    sampleWatchlist,
} from './sample'

const NOW = Date.parse('2026-10-30T14:46:00Z')
const PAGE = 8
const missingApis = new Set()

const readOrSample = async (key, request, sampleValue, accept) => {
    if (missingApis.has(key)) return { sample: true, data: sampleValue }
    try {
        const data = await request()
        if (!accept(data)) {
            missingApis.add(key)
            return { sample: true, data: sampleValue }
        }
        return { sample: false, data }
    } catch (err) {
        if (!isMissingApi(err)) throw err
        missingApis.add(key)
        return { sample: true, data: sampleValue }
    }
}

const Page = styled.div`
    padding: 4px 4px 28px;
    color: #142033;
    min-width: 0;
    max-width: 100%;
`
const Head = styled.div`
    display: flex;
    justify-content: space-between;
    gap: 16px;
    align-items: flex-end;
    margin-bottom: 14px;
    h1 { margin: 0; font-size: 22px; letter-spacing: -0.03em; }
    p { margin: 4px 0 0; color: #5c6b80; font-size: 13px; font-weight: 600; max-width: 78ch; }
`
const Seg = styled.div`
    display: inline-flex;
    flex-wrap: wrap;
    max-width: 100%;
    padding: 3px;
    background: #fff;
    border: 1px solid #e4e8f1;
    border-radius: 10px;
    button {
        border: 0;
        background: transparent;
        height: 32px;
        padding: 0 12px;
        border-radius: 8px;
        font-weight: 700;
        font-size: 13px;
        color: #5c6b80;
        cursor: pointer;
        white-space: nowrap;
    }
    button.on { background: #1b2587; color: #fff; }
`
const Tools = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    align-items: center;
    margin-bottom: 12px;
    input, select {
        height: 36px;
        border: 1px solid #d5dbe8;
        border-radius: 9px;
        background: #fff;
        padding: 0 10px;
        font-weight: 600;
        font-size: 13px;
    }
    input { min-width: 0; width: min(280px, 100%); }
    @media (max-width: 720px) {
        input, select { width: 100%; }
    }
`
const Kpis = styled.div`
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 12px;
    margin-bottom: 12px;
    @media (max-width: 1100px) { grid-template-columns: 1fr 1fr; }
    @media (max-width: 640px) { grid-template-columns: 1fr; }
`
const Kpi = styled.article`
    background: #fff;
    border: 1px solid #e4e8f1;
    border-radius: 14px;
    padding: 14px 16px;
    .label { font-size: 12px; font-weight: 700; color: #5c6b80; }
    .value { font-size: 28px; font-weight: 800; letter-spacing: -0.04em; margin-top: 4px; }
    .sub { font-size: 12px; color: #5c6b80; font-weight: 600; margin-top: 2px; }
`
const Panel = styled.section`
    background: #fff;
    border: 1px solid #e4e8f1;
    border-radius: 14px;
    overflow: hidden;
    margin-bottom: 12px;
    min-width: 0;
    max-width: 100%;
`
const PanelHead = styled.div`
    padding: 12px 14px;
    border-bottom: 1px solid #e4e8f1;
    h2 { margin: 0; font-size: 15px; }
    p { margin: 3px 0 0; color: #5c6b80; font-size: 12px; font-weight: 600; }
`
const TableWrap = styled.div`
    overflow-x: auto;
    max-width: 100%;
    -webkit-overflow-scrolling: touch;
`
const Table = styled.table`
    width: 100%;
    border-collapse: collapse;
    min-width: 720px;
    th, td { text-align: left; padding: 11px 12px; border-bottom: 1px solid #eef1f6; font-size: 13px; }
    th { font-size: 11px; letter-spacing: 0.04em; text-transform: uppercase; color: #5c6b80; background: #fafbfe; }
    .name { font-weight: 800; }
    .org { color: #5c6b80; font-weight: 600; }
`
const Pill = styled.span`
    display: inline-flex;
    align-items: center;
    height: 22px;
    padding: 0 8px;
    border-radius: 99px;
    font-size: 11.5px;
    font-weight: 800;
    background: ${p => p.$bg || '#e8edff'};
    color: ${p => p.$color || '#1b2587'};
`
const Note = styled.p`
    margin: 0;
    font-size: 12px;
    color: #5c6b80;
    font-weight: 600;
    line-height: 1.45;
`
const Banner = styled.div`
    background: #eef1fb;
    color: #1b2587;
    border-radius: 12px;
    padding: 10px 12px;
    font-size: 13px;
    font-weight: 700;
    margin-bottom: 12px;
`
const Pager = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    padding: 10px 14px;
    button {
        height: 32px;
        min-width: 32px;
        margin-left: 6px;
        border-radius: 8px;
        border: 1px solid #e4e8f1;
        background: #fff;
        font-weight: 700;
        cursor: pointer;
    }
    button.on { background: #1b2587; color: #fff; border-color: #1b2587; }
    button:disabled { opacity: 0.45; cursor: not-allowed; }
`
const Miss = styled.span`color: #8b95a8; font-weight: 700;`
const Split = styled.div`
    display: grid;
    grid-template-columns: 1.35fr 0.9fr;
    gap: 12px;
    @media (max-width: 1100px) { grid-template-columns: 1fr; }
`
const BarBtn = styled.button`
    width: 100%;
    display: grid;
    grid-template-columns: ${p => (p.$avatar ? '32px minmax(0, 1fr) auto' : '26px minmax(0, 1fr) auto')};
    gap: 10px;
    align-items: center;
    padding: 8px 14px;
    border: 0;
    border-top: 1px solid #f1f4f9;
    background: transparent;
    text-align: left;
    cursor: pointer;
    b { display: block; font-size: 13.5px; }
    small { color: #5c6b80; font-weight: 600; font-size: 12px; }
    &:hover { background: #f7f8fc; }
    @media (max-width: 560px) {
        grid-template-columns: ${p => (p.$avatar ? '32px minmax(0, 1fr)' : '26px minmax(0, 1fr)')} !important;
    }
`
const Rank = styled.span`
    width: 26px;
    height: 26px;
    border-radius: 7px;
    display: grid;
    place-items: center;
    font-size: 12px;
    font-weight: 800;
    background: ${p => (p.$top ? '#1b2587' : '#eef1f8')};
    color: ${p => (p.$top ? '#fff' : '#1b2587')};
`
const Track = styled.span`
    display: block;
    height: 7px;
    margin-top: 6px;
    background: #eef1f6;
    border-radius: 99px;
    overflow: hidden;
    i {
        display: block;
        height: 7px;
        max-height: 7px;
        border-radius: 99px;
        background: #263ba6;
    }
`
const Stat = styled.span`
    text-align: right;
    b { display: block; font-size: 15px; letter-spacing: -0.03em; }
    small { display: block; color: #5c6b80; font-weight: 600; font-size: 12px; }
    @media (max-width: 560px) {
        grid-column: 2;
        text-align: left;
        margin-top: 4px;
    }
`
const Insight = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    min-height: 42px;
    margin-bottom: 12px;
    padding: 8px 12px;
    background: #fff;
    border: 1px solid #e4e8f1;
    border-radius: 12px;
    overflow: auto;
    font-size: 12.5px;
    font-weight: 700;
    button {
        border: 0;
        background: transparent;
        padding: 0;
        font: inherit;
        font-weight: 800;
        color: #1b2587;
        white-space: nowrap;
        cursor: pointer;
    }
    button:hover { color: #263ba6; }
    .sep { width: 1px; height: 14px; background: #e4e8f1; flex: none; }
    .pulse {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: #74e1cf;
        box-shadow: 0 0 0 4px rgba(116, 225, 207, 0.28);
        flex: none;
    }
`
const SheetHead = styled.div`
    display: flex;
    justify-content: space-between;
    gap: 12px;
    align-items: center;
    padding: 12px 14px;
    border-bottom: 1px solid #e4e8f1;
    h2 { margin: 0; font-size: 15px; }
    p { margin: 3px 0 0; color: #5c6b80; font-size: 12px; font-weight: 600; }
    input {
        height: 36px;
        min-width: 0;
        width: min(240px, 100%);
        border: 1px solid #d5dbe8;
        border-radius: 10px;
        padding: 0 12px;
        font-weight: 600;
        font-size: 13px;
    }
    @media (max-width: 720px) {
        align-items: stretch;
        flex-direction: column;
        input { width: 100%; }
    }
`
const ClickRow = styled.tr`
    cursor: pointer;
    &:hover td { background: #f7f8fc; }
`
const CompanyCell = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    b { display: block; font-size: 13.5px; }
    small { color: #5c6b80; font-weight: 600; font-size: 12px; }
`
const Avatar = styled.span`
    width: ${p => (p.$lg ? 46 : 32)}px;
    height: ${p => (p.$lg ? 46 : 32)}px;
    border-radius: 50%;
    display: grid;
    place-items: center;
    color: #fff;
    font-weight: 800;
    font-size: ${p => (p.$lg ? 15 : 11)}px;
    background: ${p => p.$bg};
    flex: none;
`
const Dot = styled.i`
    display: inline-block;
    width: 7px;
    height: 7px;
    border-radius: 50%;
    margin-right: 6px;
    background: ${p => (p.$warn ? '#d97706' : '#0f7a4a')};
`
const Overlay = styled.div`
    position: fixed;
    inset: 0;
    background: rgba(14, 19, 32, 0.46);
    display: flex;
    justify-content: flex-end;
    z-index: 1200;
`
const Drawer = styled.aside`
    width: min(420px, 100%);
    height: 100%;
    background: #fff;
    box-shadow: -16px 0 50px rgba(0, 0, 0, 0.16);
    display: flex;
    flex-direction: column;
    min-height: 0;
    h3 { margin: 0; font-size: 22px; letter-spacing: -0.03em; }
    h4 { margin: 0 0 6px; font-size: 13px; }
`
const DrawerTop = styled.div`
    flex: none;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    padding: 14px 16px;
    border-bottom: 1px solid #e4e8f1;
    background: #fff;
    .kicker { font-size: 11px; font-weight: 800; letter-spacing: 0.12em; text-transform: uppercase; color: #1b2587; }
    button {
        width: 40px;
        height: 40px;
        border-radius: 10px;
        border: 1px solid #d5dbe8;
        background: #fff;
        color: #142033;
        font-size: 22px;
        line-height: 1;
        cursor: pointer;
    }
    button:hover { background: #f4f6fb; }
`
const DrawerBody = styled.div`
    flex: 1;
    min-height: 0;
    overflow: auto;
    padding: 16px 20px 28px;
`
const Facts = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
    margin-top: 14px;
    div { background: #f4f6fb; border-radius: 12px; padding: 12px; }
    span { display: block; color: #5c6b80; font-size: 11.5px; font-weight: 800; letter-spacing: 0.04em; text-transform: uppercase; }
    b { display: block; margin-top: 4px; font-size: 18px; letter-spacing: -0.03em; }
`
const Block = styled.div`
    margin-top: 14px;
    padding-top: 12px;
    border-top: 1px solid #e4e8f1;
    p { margin: 0; color: #5c6b80; font-size: 13px; font-weight: 600; line-height: 1.55; }
`
const OpenBtn = styled.button`
    width: 100%;
    height: 42px;
    margin-top: 16px;
    border: 0;
    border-radius: 10px;
    background: #1b2587;
    color: #fff;
    font-weight: 800;
    font-size: 14px;
    cursor: pointer;
`

const initials = name =>
    String(name || '')
        .split(/\s+/)
        .filter(word => word && word !== '&' && word.toLowerCase() !== 'and')
        .slice(0, 2)
        .map(word => word[0].toUpperCase())
        .join('')

const formatDuration = minutes => {
    const total = Math.max(0, Math.round(Number(minutes) || 0))
    const hours = Math.floor(total / 60)
    const rest = total % 60
    if (!hours) return `${rest}m`
    return `${hours}h ${rest}m`
}

const daysAgo = iso => {
    if (!iso) return 'No sign-in yet'
    const then = new Date(iso)
    const now = new Date(NOW)
    const startThen = Date.UTC(then.getUTCFullYear(), then.getUTCMonth(), then.getUTCDate())
    const startNow = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())
    const days = Math.round((startNow - startThen) / 86400000)
    if (days <= 0) return 'Today'
    if (days === 1) return '1 day ago'
    return `${days} days ago`
}

const joinedLabel = iso => {
    const then = Date.parse(iso)
    if (Number.isNaN(then)) return ''
    const days = Math.round((NOW - then) / 86400000)
    if (days < 45) return daysAgo(iso)
    const years = Math.floor((NOW - then) / (365.25 * 86400000))
    if (years >= 2) return `${years} years ago`
    if (years === 1) return '1 year ago'
    const months = Math.max(1, Math.round(days / 30.4))
    if (months === 1) return '1 month ago'
    return `${months} months ago`
}

const phoneText = phone => {
    if (phone) return phone
    return <Miss>Not provided</Miss>
}

const planPill = plan => {
    if (plan === 'Enterprise' || plan === 'Paid') return <Pill $bg="#e5f6ef" $color="#0f7a4a">{plan}</Pill>
    if (plan === 'Free') return <Pill $bg="#fff4df" $color="#8a5a00">{plan}</Pill>
    if (plan === 'Trial') return <Pill>{plan}</Pill>
    return <Pill $bg="#e8edff" $color="#263ba6">{plan}</Pill>
}

const breachPill = breach => {
    if (breach === 'Both') return <Pill $bg="#fdecec" $color="#b42318">{breach}</Pill>
    if (breach === 'Team') return <Pill $bg="#e5f7f4" $color="#0f6e68">{breach}</Pill>
    return <Pill>{breach}</Pill>
}

const PALETTE = ['#1b2a78', '#0f6e68', '#1e3a5f', '#3f4c8a', '#334155', '#115e59', '#243056', '#3d4f7c']

const colorFor = name => {
    const text = String(name || 'Company')
    let hash = 0
    for (let index = 0; index < text.length; index += 1) {
        hash = Math.abs((hash + text.charCodeAt(index) * (index + 1)) % PALETTE.length)
    }
    return PALETTE[hash]
}

const warnStatus = status => status === 'Trial' || status === 'Free'

const includesText = (company, search) => {
    const query = String(search || '').trim().toLowerCase()
    if (!query) return true
    return [company.CompanyName, company.OwnerName, company.OwnerEmail, company.Plan, company.Status]
        .some(value => String(value || '').toLowerCase().includes(query))
}

export const SuperAdminScreen = () => {
    const navigate = useNavigate()
    const [view, setView] = useState('overview')
    const [period, setPeriod] = useState(30)
    const [signPeriod, setSignPeriod] = useState(7)
    const [search, setSearch] = useState('')
    const [status, setStatus] = useState('All')
    const [sort, setSort] = useState('templates')
    const [breach, setBreach] = useState('All')
    const [page, setPage] = useState(1)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')
    const [sample, setSample] = useState(false)
    const [overview, setOverview] = useState(null)
    const [list, setList] = useState({ Items: [], TotalCount: 0 })
    const [picked, setPicked] = useState(null)
    const [userSort, setUserSort] = useState('time')

    useEffect(() => {
        if (!SUPER_ADMIN_FLOW_ENABLED) navigate('/app/task', { replace: true })
    }, [navigate])

    useEffect(() => {
        const onKey = event => {
            if (event.key === 'Escape') setPicked(null)
        }
        window.addEventListener('keydown', onKey)
        return () => window.removeEventListener('keydown', onKey)
    }, [])

    useEffect(() => {
        if (!picked) return undefined
        const previous = document.body.style.overflow
        document.body.style.overflow = 'hidden'
        return () => {
            document.body.style.overflow = previous
        }
    }, [picked])

    useEffect(() => {
        if (!SUPER_ADMIN_FLOW_ENABLED) return undefined
        let cancelled = false
        const run = async () => {
            setLoading(true)
            setError('')
            try {
                if (view === 'overview') {
                    const result = await readOrSample(
                        'overview',
                        () => SuperAdminDashboardService.getOverview(period),
                        sampleOverview(period),
                        data => Array.isArray(data?.TemplateRanking),
                    )
                    if (cancelled) return
                    setSample(result.sample)
                    setOverview(result.data)
                } else if (view === 'companies') {
                    const query = { pageNumber: page, pageSize: PAGE, search, status, sort, sortDir: 'desc' }
                    const result = await readOrSample(
                        'companies',
                        () => SuperAdminDashboardService.listCompanies(query),
                        sampleCompanies(query),
                        data => Array.isArray(data?.Items),
                    )
                    if (cancelled) return
                    setSample(result.sample)
                    setList(result.data)
                } else if (view === 'signins') {
                    const query = { periodDays: signPeriod, pageNumber: page, pageSize: PAGE, search }
                    const result = await readOrSample(
                        'signins',
                        () => SuperAdminDashboardService.listSignIns(query),
                        sampleSignIns(query),
                        data => Array.isArray(data?.Items),
                    )
                    if (cancelled) return
                    setSample(result.sample)
                    setList(result.data)
                } else {
                    const query = { breach, pageNumber: page, pageSize: PAGE, search }
                    const result = await readOrSample(
                        'watch',
                        () => SuperAdminDashboardService.listWatchlist(query),
                        sampleWatchlist(query),
                        data => Array.isArray(data?.Items),
                    )
                    if (cancelled) return
                    setSample(result.sample)
                    setList(result.data)
                }
            } catch (err) {
                if (cancelled) return
                const body = err?.error
                setError(body?.Message || body?.message || 'The Super Admin data could not be loaded.')
            } finally {
                if (!cancelled) setLoading(false)
            }
        }
        run()
        return () => {
            cancelled = true
        }
    }, [view, period, signPeriod, search, status, sort, breach, page])

    const pages = Math.max(1, Math.ceil((list.TotalCount || 0) / PAGE))
    const ranked = overview?.TemplateRanking || []
    const visibleCompanies = ranked.filter(company => includesText(company, search))
    const maxTemplates = Math.max(...ranked.map(company => company.Templates), 1)
    const signInTotal = ranked.reduce((sum, company) => sum + (Number(company.SignIns) || 0), 0)
    const people = [...(overview?.UsersByTime || [])].sort((a, b) => (
        userSort === 'sessions' ? b.Sessions - a.Sessions : b.Minutes - a.Minutes
    ))
    const maxPeople = Math.max(...people.map(person => (userSort === 'sessions' ? person.Sessions : person.Minutes)), 1)

    const openCompany = company => {
        if (!company) return
        const index = ranked.findIndex(item => item.CompanyId === company.CompanyId)
        const topUser = (overview?.UsersByTime || []).find(person => person.CompanyName === company.CompanyName) || null
        setPicked({
            company,
            rank: index >= 0 ? index + 1 : null,
            total: ranked.length || overview?.CompanyCount || 0,
            topUser,
        })
    }

    if (!SUPER_ADMIN_FLOW_ENABLED) return null

    return (
        <Page>
            <Head>
                <div>
                    <h1>Super Admin</h1>
                    <p>
                        All companies, who uses the most templates, the latest companies, and who spends the most time.
                        Recent sign-ins and the unlicensed watchlist are on the same page. Nothing here edits a customer.
                    </p>
                </div>
            </Head>
            <Tools>
                <Seg>
                    {[
                        ['overview', 'Overview'],
                        ['companies', 'Companies'],
                        ['signins', 'Recent sign-ins'],
                        ['watch', 'Unlicensed watchlist'],
                    ].map(([id, label]) => (
                        <button
                            key={id}
                            type="button"
                            className={view === id ? 'on' : ''}
                            onClick={() => {
                                setView(id)
                                setPage(1)
                                setSearch('')
                                setPicked(null)
                            }}
                        >
                            {label}
                        </button>
                    ))}
                </Seg>
            </Tools>
            {sample && (
                <Banner>
                    Sample data is showing because the Super Admin APIs are not on the server yet. The page will use the live response as soon as those GET endpoints exist.
                </Banner>
            )}
            {error && <Banner>{error}</Banner>}
            {loading && <Panel><PanelHead><h2>Loading</h2></PanelHead></Panel>}
            {!loading && !error && view === 'overview' && overview && (
                <>
                    <Tools>
                        <Seg>
                            {[7, 30, 90].map(days => (
                                <button key={days} type="button" className={period === days ? 'on' : ''} onClick={() => setPeriod(days)}>
                                    {days} days
                                </button>
                            ))}
                        </Seg>
                    </Tools>
                    <Insight>
                        <span className="pulse" />
                        <span>{overview.CompanyCount} companies</span>
                        <span className="sep" />
                        <button type="button" onClick={() => openCompany(ranked[0])}>
                            Most templates · {ranked[0]?.CompanyName} · {ranked[0]?.Templates}
                        </button>
                        <span className="sep" />
                        <button type="button" onClick={() => openCompany(overview.LatestCompany)}>
                            Newest · {overview.LatestCompany?.CompanyName} · {daysAgo(overview.LatestCompany?.JoinedOn)}
                        </button>
                        <span className="sep" />
                        <button type="button" onClick={() => openCompany(ranked.find(company => company.CompanyName === overview.TopUser?.CompanyName) || ranked[0])}>
                            Most time · {overview.TopUser?.Name} · {formatDuration(overview.TopUser?.Minutes)}
                        </button>
                    </Insight>
                    <Kpis>
                        <Kpi><div className="label">Companies</div><div className="value">{overview.CompanyCount}</div><div className="sub">Every company on the platform</div></Kpi>
                        <Kpi><div className="label">Templates in use</div><div className="value">{overview.TemplateCount}</div><div className="sub">Active templates. This does not change with the date range.</div></Kpi>
                        <Kpi><div className="label">Sign-ins</div><div className="value">{signInTotal}</div><div className="sub">In the last {period} days</div></Kpi>
                        <Kpi><div className="label">Most time</div><div className="value" style={{ fontSize: 22 }}>{formatDuration(overview.TopUser?.Minutes)}</div><div className="sub">{overview.TopUser?.Name} · {overview.TopUser?.CompanyName}</div></Kpi>
                    </Kpis>
                    <Split>
                        <Panel>
                            <PanelHead>
                                <h2>Which company is using more templates</h2>
                                <p>Ranked by active templates. The count stays the same for 7, 30, and 90 days.</p>
                            </PanelHead>
                            {ranked.map((company, index) => (
                                <BarBtn key={company.CompanyId} type="button" onClick={() => openCompany(company)}>
                                    <Rank $top={index === 0}>{index + 1}</Rank>
                                    <span>
                                        <b>{company.CompanyName}</b>
                                        <Track><i style={{ width: `${Math.max(8, Math.round((company.Templates / maxTemplates) * 100))}%` }} /></Track>
                                    </span>
                                    <Stat><b>{company.Templates}</b><small>{company.Plan} · {company.Members} users</small></Stat>
                                </BarBtn>
                            ))}
                        </Panel>
                        <Panel>
                            <PanelHead>
                                <h2>Latest companies</h2>
                                <p>Newest accounts to join the platform.</p>
                            </PanelHead>
                            {(overview.LatestCompanies || []).map(company => (
                                <BarBtn $avatar key={company.CompanyId} type="button" onClick={() => openCompany(company)}>
                                    <Avatar $bg={colorFor(company.CompanyName)}>{initials(company.CompanyName)}</Avatar>
                                    <span>
                                        <b>{company.CompanyName}</b>
                                        <small>{company.Plan} · {company.Templates} templates</small>
                                    </span>
                                    <Stat><b>{joinedLabel(company.JoinedOn)}</b><small>{company.JoinedOn}</small></Stat>
                                </BarBtn>
                            ))}
                        </Panel>
                    </Split>
                    <Panel>
                        <SheetHead>
                            <div>
                                <h2>Which user is using more time</h2>
                                <p>
                                    {userSort === 'sessions'
                                        ? `Ranked by number of sign-ins in the last ${period} days.`
                                        : `Ranked by time on the platform in the last ${period} days.`}
                                </p>
                            </div>
                            <Seg>
                                <button type="button" className={userSort === 'time' ? 'on' : ''} onClick={() => setUserSort('time')}>By time</button>
                                <button type="button" className={userSort === 'sessions' ? 'on' : ''} onClick={() => setUserSort('sessions')}>By sign-ins</button>
                            </Seg>
                        </SheetHead>
                        <TableWrap>
                            <Table>
                                <thead>
                                    <tr>
                                        <th>User</th><th>Company</th><th>Sign-ins</th><th>Time on platform</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {people.map((person, index) => {
                                        const width = Math.round(((userSort === 'sessions' ? person.Sessions : person.Minutes) / maxPeople) * 100)
                                        const company = ranked.find(item => item.CompanyName === person.CompanyName)
                                        return (
                                            <ClickRow key={person.UserId} onClick={() => openCompany(company)}>
                                                <td>
                                                    <CompanyCell>
                                                        <Avatar $bg={colorFor(person.Name)}>{initials(person.Name)}</Avatar>
                                                        <span>
                                                            <b>{person.Name}</b>
                                                            <small>{person.Email}</small>
                                                        </span>
                                                    </CompanyCell>
                                                </td>
                                                <td>{person.CompanyName}</td>
                                                <td>{person.Sessions}</td>
                                                <td>
                                                    {formatDuration(person.Minutes)}
                                                    {index === 0 && userSort === 'time' ? ' · Most time' : ''}
                                                    <Track><i style={{ width: `${Math.max(8, width)}%` }} /></Track>
                                                </td>
                                            </ClickRow>
                                        )
                                    })}
                                </tbody>
                            </Table>
                        </TableWrap>
                    </Panel>
                    <Panel>
                        <SheetHead>
                            <div>
                                <h2>All companies</h2>
                                <p>Ranked by templates. Search to narrow the list. A row opens the detail on the side.</p>
                            </div>
                            <input value={search} placeholder="Search companies" onChange={event => setSearch(event.target.value)} />
                        </SheetHead>
                        <TableWrap>
                            <Table>
                                <thead>
                                    <tr>
                                        <th>Company</th><th>Plan</th><th>Users</th><th>Templates</th><th>Sign-ins</th><th>Owner</th><th>Joined</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {visibleCompanies.map(company => (
                                        <ClickRow key={company.CompanyId} onClick={() => openCompany(company)}>
                                            <td>
                                                <CompanyCell>
                                                    <Avatar $bg={colorFor(company.CompanyName)}>{initials(company.CompanyName)}</Avatar>
                                                    <span>
                                                        <b>{company.CompanyName}</b>
                                                        <small><Dot $warn={warnStatus(company.Status)} />{company.Status}</small>
                                                    </span>
                                                </CompanyCell>
                                            </td>
                                            <td>{planPill(company.Plan)}</td>
                                            <td>{company.Members}</td>
                                            <td>{company.Templates}</td>
                                            <td>{company.SignIns}</td>
                                            <td>{company.OwnerName}</td>
                                            <td>{joinedLabel(company.JoinedOn)}</td>
                                        </ClickRow>
                                    ))}
                                    {!visibleCompanies.length && (
                                        <tr><td colSpan={7}>No company matches that search.</td></tr>
                                    )}
                                </tbody>
                            </Table>
                        </TableWrap>
                    </Panel>
                </>
            )}
            {!loading && !error && view === 'companies' && (
                <Panel>
                    <PanelHead><h2>All companies</h2><p>{list.TotalCount} companies. Click a row to open the detail on the side.</p></PanelHead>
                    <Tools style={{ padding: '12px 14px 0' }}>
                        <input value={search} placeholder="Search company, owner, or email" onChange={event => { setSearch(event.target.value); setPage(1) }} />
                        <select value={status} onChange={event => { setStatus(event.target.value); setPage(1) }}>
                            {['All', 'Active', 'Trial', 'Free'].map(item => <option key={item}>{item}</option>)}
                        </select>
                        <Seg>
                            <button type="button" className={sort === 'templates' ? 'on' : ''} onClick={() => setSort('templates')}>Templates</button>
                            <button type="button" className={sort === 'joined' ? 'on' : ''} onClick={() => setSort('joined')}>Latest</button>
                            <button type="button" className={sort === 'name' ? 'on' : ''} onClick={() => setSort('name')}>Name</button>
                        </Seg>
                    </Tools>
                    <TableWrap>
                        <Table>
                            <thead>
                                <tr>
                                    <th>Company</th><th>Plan</th><th>Templates</th><th>Members</th><th>Owner</th><th>Phone</th><th>Joined</th>
                                </tr>
                            </thead>
                            <tbody>
                                {list.Items.map(company => (
                                    <ClickRow key={company.CompanyId} onClick={() => openCompany(company)}>
                                        <td>
                                            <CompanyCell>
                                                <Avatar $bg={colorFor(company.CompanyName)}>{initials(company.CompanyName)}</Avatar>
                                                <span>
                                                    <b>{company.CompanyName}</b>
                                                    <small><Dot $warn={warnStatus(company.Status)} />{company.Status}</small>
                                                </span>
                                            </CompanyCell>
                                        </td>
                                        <td>{planPill(company.Plan)}</td>
                                        <td>{company.Templates}</td>
                                        <td>{company.Members}</td>
                                        <td><div className="name">{company.OwnerName}</div><div className="org">{company.OwnerEmail}</div></td>
                                        <td>{phoneText(company.OwnerPhone)}</td>
                                        <td>{joinedLabel(company.JoinedOn)}</td>
                                    </ClickRow>
                                ))}
                            </tbody>
                        </Table>
                    </TableWrap>
                    <ListPager page={page} pages={pages} total={list.TotalCount} onPage={setPage} />
                </Panel>
            )}
            {!loading && !error && view === 'signins' && (
                <Panel>
                    <PanelHead>
                        <h2>Recent sign-ins</h2>
                        <p>One row per person. Events are each authentication. Times are America/New_York.</p>
                    </PanelHead>
                    <Kpis style={{ padding: '12px 14px 0' }}>
                        <Kpi><div className="label">Unique users</div><div className="value">{list.UniqueUsers || 0}</div><div className="sub">People, not events</div></Kpi>
                        <Kpi><div className="label">Sign-in events</div><div className="value">{list.Events || 0}</div><div className="sub">Web and mobile, each one counted</div></Kpi>
                        <Kpi><div className="label">New registrations</div><div className="value">{list.NewRegistrations || 0}</div><div className="sub">Accounts created in this period</div></Kpi>
                        <Kpi><div className="label">Unlicensed sign-ins</div><div className="value">{list.UnlicensedSignedIn || 0}</div><div className="sub">Free or trial, no paid license</div></Kpi>
                    </Kpis>
                    <Tools style={{ padding: '0 14px' }}>
                        <Seg>
                            {[1, 7, 30].map(days => (
                                <button key={days} type="button" className={signPeriod === days ? 'on' : ''} onClick={() => { setSignPeriod(days); setPage(1) }}>{days} day{days > 1 ? 's' : ''}</button>
                            ))}
                        </Seg>
                        <input value={search} placeholder="Search name, email, or company" onChange={event => { setSearch(event.target.value); setPage(1) }} />
                    </Tools>
                    <TableWrap>
                        <Table>
                            <thead>
                                <tr><th>User</th><th>Company</th><th>License</th><th>Last sign-in</th><th>Method</th><th>Events</th></tr>
                            </thead>
                            <tbody>
                                {list.Items.map(person => (
                                    <tr key={person.UserId}>
                                        <td><div className="name">{person.Name}</div><div className="org">{person.Email} · {phoneText(person.Phone)}</div></td>
                                        <td>{person.CompanyName}</td>
                                        <td>{planPill(person.License)}</td>
                                        <td>{daysAgo(person.LastSignIn)}</td>
                                        <td>{person.Method} · {person.Platform}</td>
                                        <td>{person.Events}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </TableWrap>
                    <ListPager page={page} pages={pages} total={list.TotalCount} onPage={setPage} />
                    <div style={{ padding: '0 14px 12px' }}><Note>A person with several sign-ins is still one row. The event count is beside them.</Note></div>
                </Panel>
            )}
            {!loading && !error && view === 'watch' && (
                <Panel>
                    <PanelHead>
                        <h2>Unlicensed usage watchlist</h2>
                        <p>No paid license, and templates are 3 or more, or members are 2 or more. Members include the owner. The rule is OR, not AND.</p>
                    </PanelHead>
                    <Kpis style={{ padding: '12px 14px 0' }}>
                        <Kpi><div className="label">Flagged</div><div className="value">{list.Flagged || 0}</div><div className="sub">Template + team − both</div></Kpi>
                        <Kpi><div className="label">Templates</div><div className="value">{list.TemplateThreshold || 0}</div><div className="sub">3 or more active templates</div></Kpi>
                        <Kpi><div className="label">Team</div><div className="value">{list.TeamThreshold || 0}</div><div className="sub">2 or more active members</div></Kpi>
                        <Kpi><div className="label">Both</div><div className="value">{list.Both || 0}</div><div className="sub">Already counted in the other two</div></Kpi>
                    </Kpis>
                    <Tools style={{ padding: '0 14px' }}>
                        <Seg>
                            {['All', 'Templates', 'Team', 'Both'].map(item => (
                                <button key={item} type="button" className={breach === item ? 'on' : ''} onClick={() => { setBreach(item); setPage(1) }}>{item}</button>
                            ))}
                        </Seg>
                        <input value={search} placeholder="Search name, email, or company" onChange={event => { setSearch(event.target.value); setPage(1) }} />
                    </Tools>
                    <TableWrap>
                        <Table>
                            <thead>
                                <tr><th>Account</th><th>Company</th><th>Templates</th><th>Team</th><th>Why</th><th>Phone</th><th>Last sign-in</th></tr>
                            </thead>
                            <tbody>
                                {list.Items.map(person => (
                                    <tr key={person.UserId}>
                                        <td><div className="name">{person.Name}</div><div className="org">{person.Email}</div></td>
                                        <td>{person.CompanyName}</td>
                                        <td>{person.Templates}</td>
                                        <td>{person.Team}</td>
                                        <td>{breachPill(person.Breach)}</td>
                                        <td>{phoneText(person.Phone)}</td>
                                        <td>{daysAgo(person.LastSignIn)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </TableWrap>
                    <ListPager page={page} pages={pages} total={list.TotalCount} onPage={setPage} />
                    <div style={{ padding: '0 14px 12px' }}>
                        <Note>Paid accounts stay off this list. Opening a row does not edit the customer. Export stays off until it uses the same staff authorization.</Note>
                    </div>
                </Panel>
            )}
            {picked && createPortal(
                <Overlay onClick={() => setPicked(null)}>
                    <Drawer role="dialog" aria-label="Company details" onClick={event => event.stopPropagation()}>
                        <DrawerTop>
                            <span className="kicker">Company</span>
                            <button type="button" aria-label="Close panel" onClick={() => setPicked(null)}>×</button>
                        </DrawerTop>
                        <DrawerBody>
                        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                            <Avatar $lg $bg={colorFor(picked.company.CompanyName)}>{initials(picked.company.CompanyName)}</Avatar>
                            <div>
                                <h3>{picked.company.CompanyName}</h3>
                                <div style={{ marginTop: 6, display: 'flex', gap: 8, alignItems: 'center' }}>
                                    {planPill(picked.company.Plan)}
                                    <span style={{ color: '#5c6b80', fontWeight: 600, fontSize: 13 }}>
                                        <Dot $warn={warnStatus(picked.company.Status)} />{picked.company.Status}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <Facts>
                            <div><span>Templates</span><b>{picked.company.Templates}</b></div>
                            <div><span>Template rank</span><b>{picked.rank ? `${picked.rank} of ${picked.total}` : '—'}</b></div>
                            <div><span>Users</span><b>{picked.company.Members}</b></div>
                            <div><span>Sign-ins</span><b>{picked.company.SignIns}</b></div>
                        </Facts>
                        <Block>
                            <h4>Account</h4>
                            <p>
                                Joined {picked.company.JoinedOn} · {joinedLabel(picked.company.JoinedOn)}
                                <br />
                                Account owner {picked.company.OwnerName}
                                <br />
                                {picked.company.OwnerEmail}
                                <br />
                                Phone {phoneText(picked.company.OwnerPhone)}
                            </p>
                        </Block>
                        {picked.topUser && (
                            <Block>
                                <h4>Signed in the most here</h4>
                                <CompanyCell>
                                    <Avatar $bg={colorFor(picked.topUser.Name)}>{initials(picked.topUser.Name)}</Avatar>
                                    <span>
                                        <b>{picked.topUser.Name}</b>
                                        <small>{formatDuration(picked.topUser.Minutes)} · {picked.topUser.Sessions} sessions</small>
                                    </span>
                                </CompanyCell>
                            </Block>
                        )}
                        <OpenBtn
                            type="button"
                            onClick={() => {
                                setView('companies')
                                setSearch(picked.company.CompanyName)
                                setPage(1)
                                setStatus('All')
                            }}
                        >
                            Open this company
                        </OpenBtn>
                        </DrawerBody>
                    </Drawer>
                </Overlay>,
                document.body,
            )}
        </Page>
    )
}

const ListPager = ({ page, pages, total, onPage }) => (
    <Pager>
        <span>{total} shown</span>
        <div>
            <button type="button" disabled={page <= 1} onClick={() => onPage(page - 1)}>Previous</button>
            {Array.from({ length: pages }, (_, index) => (
                <button key={index + 1} type="button" className={page === index + 1 ? 'on' : ''} onClick={() => onPage(index + 1)}>{index + 1}</button>
            ))}
            <button type="button" disabled={page >= pages} onClick={() => onPage(page + 1)}>Next</button>
        </div>
    </Pager>
)
